import type { Project, RequirementSetId, DocumentType } from '../../types';
import { apiClient, handleApiError, type SortOrder } from '../api-client';

/**
 * Get projects with pagination
 */
export async function getProjects(page: number, limit: number, sortBy?: string, sortOrder?: SortOrder) {
  try {
    return apiClient.getPaginated<Project>('/projects', { page, limit, sortBy, sortOrder });
  } catch (error) {
    return handleApiError(error, 'Failed to fetch projects');
  }
}

/**
 * Get all projects in the system (admin only)
 */
export async function getAllProjects(
  page: number,
  limit: number,
  sortBy?: string,
  sortOrder?: SortOrder,
  query?: string
) {
  try {
    const fetchOpts: { params?: Record<string, string> } = {};
    if (query) {
      fetchOpts.params = { query };
    }

    return apiClient.getPaginated<Project>('/projects/all', { page, limit, sortBy, sortOrder }, fetchOpts);
  } catch (error) {
    return handleApiError(error, 'Failed to fetch all projects');
  }
}

/**
 * Get a project by ID
 */
export async function getProject(projectId: string) {
  try {
    return apiClient.get<Project>(`/projects/${projectId}`);
  } catch (error) {
    return handleApiError(error, 'Failed to fetch project');
  }
}

/**
 * Create a new project
 */
export async function createProject(
  name: string,
  files: Array<{ file: File; type: DocumentType }>,
  requirementSetId: RequirementSetId,
  paymentIntentId?: string
): Promise<Project> {
  try {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('requirementSetId', requirementSetId);
    if (paymentIntentId) {
      formData.append('paymentIntentId', paymentIntentId);
    }

    // Append each file and its type
    files.forEach((fileData) => {
      formData.append('files', fileData.file);
      formData.append('fileTypes', fileData.type.toString());
    });

    return apiClient.post<Project>('/projects', formData);
  } catch (error) {
    return handleApiError(error, 'Error creating project');
  }
}

/**
 * Update a project
 */
export async function updateProject(id: string, updateData: { name?: string }): Promise<Project> {
  try {
    return apiClient.patch<Project>(`/projects/${id}`, updateData);
  } catch (error) {
    return handleApiError(error, 'Error updating project');
  }
}

/**
 * Delete a project
 */
export async function deleteProject(id: string): Promise<void> {
  try {
    await apiClient.delete(`/projects/${id}`);
  } catch (error) {
    return handleApiError(error, 'Error deleting project');
  }
}

export const downloadDocument = async (endpoint: string, fileName: string): Promise<void> => {
  try {
    const blob = await apiClient.getBlob(endpoint);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    return handleApiError(error, 'Failed to download document');
  }
};

/**
 * Build the URL for serving a project document.
 *
 * @param projectId ID of the project
 * @param documentId ID of the document (MongoDB ObjectId)
 */
export function getDocumentServeUrl(projectId: string, documentId: string): string {
  return `/projects/${projectId}/documents/${documentId}`;
}

/**
 * Download a document from a project
 * @param projectId ID of the project
 * @param documentId ID of the document to download
 */
export const downloadProjectDocument = async (projectId: string, documentId: string): Promise<void> => {
  await downloadDocument(getDocumentServeUrl(projectId, documentId), `document.pdf`);
};

/**
 * Submit a project for verification
 */
export async function submitProjectForVerification(projectId: string, paymentIntentId?: string): Promise<Project> {
  try {
    return apiClient.post<Project>(
      `/projects/${projectId}/submit-for-verification`,
      paymentIntentId ? { paymentIntentId } : {}
    );
  } catch (error) {
    return handleApiError(error, 'Failed to submit project for verification');
  }
}

/**
 * Start review for a project
 */
export async function startProjectReview(projectId: string): Promise<Project> {
  try {
    return apiClient.post<Project>(`/projects/${projectId}/start-review`);
  } catch (error) {
    return handleApiError(error, 'Failed to start project review');
  }
}

/**
 * Approve a project
 */
export async function approveProject(projectId: string): Promise<Project> {
  try {
    return apiClient.post<Project>(`/projects/${projectId}/approve`);
  } catch (error) {
    return handleApiError(error, 'Failed to approve project');
  }
}

/**
 * Request changes for a project
 */
export async function requestProjectChanges(projectId: string): Promise<Project> {
  try {
    return apiClient.post<Project>(`/projects/${projectId}/request-changes`);
  } catch (error) {
    return handleApiError(error, 'Failed to request changes for project');
  }
}
